import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PlantillaEscritoDto } from '@models/plantillaEscrito/planillaEscritoDto.model';
import { EditorStateService } from '@services/editorState/editor-state.service';
import { EscritoService } from '@services/escrito/escrito.service';
import { PlantillaEscritoService } from '@services/plantillaEscrito/plantillaEscrito.service';
import { ConfirmDialogComponent } from '@views/escrito-editor/dialogs/confirm-dialog/confirm-dialog/confirm-dialog.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject, debounceTime, distinctUntilChanged, takeUntil, catchError, of } from 'rxjs';

@Component({
  selector: 'app-plantillas-panel',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    NgxSpinnerModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatInputModule,
    MatCardModule,
    MatMenuModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule
  ],
  templateUrl: './plantillas-panel.component.html',
  styleUrl: './plantillas-panel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlantillasPanelComponent implements OnInit, OnDestroy {

  // FormControl con validaciones
  searchControl = new FormControl('', [
    Validators.maxLength(100),
    this.noScriptValidator,
    this.noSqlInjectionValidator
  ]);

  plantillas: PlantillaEscritoDto[] = [];
  filteredPlantillas: PlantillaEscritoDto[] = [];
  isLoading = false;
  selectedPlantilla: PlantillaEscritoDto | null = null;
  searchError = false;

  private destroy$ = new Subject<void>();

  constructor(
    private plantillaService: PlantillaEscritoService,
    private editorStateService: EditorStateService,
    private escritoService: EscritoService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadPlantillas();
    this.setupSearchValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============ VALIDADORES PERSONALIZADOS ============
  private noScriptValidator(control: any) {
    if (!control.value) return null;

    const scriptPattern = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
    const hasScript = scriptPattern.test(control.value);

    return hasScript ? { 'script': { value: control.value } } : null;
  }

  private noSqlInjectionValidator(control: any) {
    if (!control.value) return null;

    const sqlPattern = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION)\b)/gi;
    const hasSql = sqlPattern.test(control.value);

    return hasSql ? { 'sql': { value: control.value } } : null;
  }

  // ============ CONFIGURACIÓN DE BÚSQUEDA ============
  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.filterPlantillas(searchTerm || '');
        this.cdr.markForCheck();
      });
  }

  private setupSearchValidation(): void {
    this.searchControl.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.searchError = status === 'INVALID';
        this.cdr.markForCheck();
      });
  }

  // ============ CARGA DE DATOS ============
  private loadPlantillas(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.plantillaService.getAll()
      .pipe(
        catchError(error => {
          console.error('Error cargando plantillas:', error);
          this.showErrorMessage('Error cargando plantillas. Intente nuevamente.');
          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (plantillas) => {
          this.plantillas = this.validateAndSortPlantillas(plantillas);
          this.filteredPlantillas = [...this.plantillas];
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private validateAndSortPlantillas(plantillas: PlantillaEscritoDto[]): PlantillaEscritoDto[] {
    return plantillas
      .filter(p => p && p.id) // Filtrar plantillas válidas
      .sort((a, b) => {
        // Ordenar por fecha de creación (más recientes primero)
        const fechaA = new Date(a.fechaCreacion || 0).getTime();
        const fechaB = new Date(b.fechaCreacion || 0).getTime();
        return fechaB - fechaA;
      });
  }

  // ============ FILTRADO ============
  private filterPlantillas(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredPlantillas = [...this.plantillas];
      return;
    }

    if (this.searchControl.invalid) {
      this.filteredPlantillas = [];
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    this.filteredPlantillas = this.plantillas.filter(plantilla =>
      this.plantillaMatchesSearch(plantilla, searchLower)
    );
  }

  private plantillaMatchesSearch(plantilla: PlantillaEscritoDto, searchTerm: string): boolean {
    return (
      (plantilla.titulo?.toLowerCase().includes(searchTerm)) ||
      (plantilla.descripcion?.toLowerCase().includes(searchTerm)) ||
      (plantilla.tipoEscrito?.descripcion?.toLowerCase().includes(searchTerm)) ||
      (this.getPlantillaCreador(plantilla)?.toLowerCase().includes(searchTerm))
    );
  }

  // ============ EVENTOS DE PLANTILLA ============
  onPlantillaClick(plantilla: PlantillaEscritoDto): void {
    if (!plantilla?.id) {
      this.showErrorMessage('Plantilla inválida');
      return;
    }
    this.selectedPlantilla = plantilla;
    this.cdr.markForCheck();
  }

  onPlantillaDoubleClick(plantilla: PlantillaEscritoDto): void {
    if (!this.validatePlantilla(plantilla)) return;
    this.openPlantillaInEditor(plantilla);
  }

  private openPlantillaInEditor(plantilla: PlantillaEscritoDto): void {
    try {
      this.editorStateService.openTab({
        tipo: 'plantilla',
        entityId: plantilla.id,
        titulo: plantilla.titulo || `Plantilla ${plantilla.id}`,
        contenido: plantilla.cuerpoHTML || '',
        entity: plantilla
      });
    } catch (error) {
      console.error('Error abriendo plantilla:', error);
      this.showErrorMessage('Error abriendo la plantilla en el editor');
    }
  }

  // ============ OPERACIONES DE PLANTILLA ============
  onUseAsEscrito(plantilla: PlantillaEscritoDto): void {
    if (!this.validatePlantilla(plantilla)) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Crear Escrito desde Plantilla',
        message: `¿Deseas crear un nuevo escrito usando la plantilla "${plantilla.titulo}"?`,
        confirmText: 'Crear Escrito',
        cancelText: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.createEscritoFromPlantilla(plantilla);
      }
    });
  }

  private createEscritoFromPlantilla(plantilla: PlantillaEscritoDto): void {
    this.escritoService.copyByPlantilla(plantilla.id, `${plantilla.titulo} - Copia`)
      .pipe(
        catchError(error => {
          console.error('Error creando escrito desde plantilla:', error);
          this.showErrorMessage('Error creando escrito desde plantilla');
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (nuevoEscrito) => {
          if (nuevoEscrito) {
            this.showSuccessMessage('Escrito creado desde plantilla exitosamente');
            this.editorStateService.openTab({
              tipo: 'escrito',
              entityId: nuevoEscrito.id,
              titulo: nuevoEscrito.titulo || 'Nuevo Escrito',
              contenido: nuevoEscrito.cuerpoHTML || '',
              entity: nuevoEscrito
            });
          }
        }
      });
  }

  onDuplicatePlantilla(plantilla: PlantillaEscritoDto): void {
    if (!this.validatePlantilla(plantilla)) return;

    this.plantillaService.clone(plantilla.id)
      .pipe(
        catchError(error => {
          console.error('Error duplicando plantilla:', error);
          this.showErrorMessage('Error al duplicar la plantilla');
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.showSuccessMessage('Plantilla duplicada exitosamente');
            this.loadPlantillas();
          }
        }
      });
  }

  onDeletePlantilla(plantilla: PlantillaEscritoDto): void {
    if (!this.validatePlantilla(plantilla)) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Plantilla',
        message: `¿Estás seguro de que deseas eliminar la plantilla "${plantilla.titulo}"?`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deletePlantilla(plantilla);
      }
    });
  }

  private deletePlantilla(plantilla: PlantillaEscritoDto): void {
    this.plantillaService.delete(plantilla.id)
      .pipe(
        catchError(error => {
          console.error('Error eliminando plantilla:', error);
          this.showErrorMessage('Error al eliminar la plantilla');
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          if (result !== null) {
            this.showSuccessMessage('Plantilla eliminada exitosamente');
            this.loadPlantillas();
            if (this.selectedPlantilla?.id === plantilla.id) {
              this.selectedPlantilla = null;
            }
          }
        }
      });
  }

  // ============ ACCIONES DEL PANEL ============
  onNewPlantilla(): void {
    try {
      this.editorStateService.openTab({
        tipo: 'plantilla',
        titulo: 'Nueva Plantilla',
        contenido: ''
      });
    } catch (error) {
      console.error('Error creando nueva plantilla:', error);
      this.showErrorMessage('Error creando nueva plantilla');
    }
  }

  onRefresh(): void {
    this.selectedPlantilla = null;
    this.searchControl.setValue('');
    this.loadPlantillas();
  }

  onToggleFilters(): void {
    this.cdr.markForCheck();
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.searchControl.markAsUntouched();
  }

  // ============ UTILIDADES ============
  private validatePlantilla(plantilla: PlantillaEscritoDto): boolean {
    if (!plantilla || !plantilla.id) {
      this.showErrorMessage('Plantilla inválida');
      return false;
    }
    return true;
  }

  getPlantillaTipo(plantilla: PlantillaEscritoDto): string {
    return plantilla?.tipoEscrito?.descripcion || 'Sin Tipo';
  }

  getPlantillaCreador(plantilla: PlantillaEscritoDto): string {
    // Implementar lógica para obtener el creador
    return 'Usuario'; // Placeholder
  }

  formatDate(date: Date): string {
    if (!date) return 'Sin fecha';

    try {
      return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'Fecha inválida';
    }
  }

  // ============ MENSAJES ============
  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  // ============ GETTERS PARA TEMPLATE ============
  get hasSearchErrors(): boolean {
    return this.searchControl.invalid && this.searchControl.touched;
  }

  get searchErrorMessage(): string {
    if (this.searchControl.hasError('script')) {
      return 'No se permiten scripts en la búsqueda';
    }
    if (this.searchControl.hasError('sql')) {
      return 'No se permiten comandos SQL en la búsqueda';
    }
    if (this.searchControl.hasError('maxlength')) {
      return 'Máximo 100 caracteres';
    }
    return '';
  }

  get panelClasses(): string {
    const classes = [];
    if (this.searchError) classes.push('search-error');
    if (this.searchControl.valid && this.searchControl.value) classes.push('search-valid');
    return classes.join(' ');
  }

  // ============ TRACKBY FUNCTIONS ============
  trackByPlantillaId(index: number, plantilla: PlantillaEscritoDto): any {
    return plantilla?.id || index;
  }
}
